import { Text } from 'tamagui';

export type FormMessageType = 'error' | 'success' | 'info' | 'warning';

export interface FormMessageProps {
  type: FormMessageType;
  message: string | null | undefined;
  /**
   * Optional id — `FormRow` populates this so the message is the
   * target of the input's `aria-describedby`. Standalone callers can
   * omit it.
   */
  id?: string;
}

const TYPE_COLOR: Record<FormMessageType, string> = {
  error: '$textError',
  success: '$textSuccess',
  info: '$textSubdued',
  warning: '$textWarning',
};

const TYPE_ROLE: Record<FormMessageType, 'alert' | 'status'> = {
  // Errors are assertive — interrupt the user.
  error: 'alert',
  // Success / info / warning announce politely without interruption.
  success: 'status',
  info: 'status',
  warning: 'status',
};

const TYPE_LIVE: Record<FormMessageType, 'polite' | 'assertive'> = {
  error: 'polite',
  success: 'polite',
  info: 'polite',
  warning: 'polite',
};

/**
 * Inline form-validation message. Per ADR-0075:
 * - `role="alert"` (errors) or `role="status"` (success/info/warning).
 * - `aria-live="polite"` so screen readers announce the message when it
 *   appears — without interrupting the user mid-utterance.
 * - Color tokens read from Phase 1's `$textError` / `$textSuccess` /
 *   `$textWarning` / `$textSubdued` — light/dark parity.
 *
 * Renders nothing when `message` is falsy.
 */
export function FormMessage({ type, message, id }: FormMessageProps) {
  if (!message) return null;
  return (
    <Text
      id={id}
      // RN-web reflects these to ARIA. On native, accessibilityLiveRegion
      // is the closest analog.
      role={TYPE_ROLE[type]}
      aria-live={TYPE_LIVE[type]}
      accessibilityLiveRegion={TYPE_LIVE[type]}
      color={TYPE_COLOR[type]}
      fontSize="$2"
    >
      {message}
    </Text>
  );
}
